/**
 * Den zweiten Faktor einrichten.
 *
 * Freiwillig und je Konto. Wer ihn einrichtet, hat ihn; wer nicht, meldet
 * sich weiter an wie bisher. Eine Pflicht wäre erst dann richtig, wenn
 * jede Einrichtung nachweislich geklappt hat — vorher sperrt sie Leute
 * aus, die gerade keine App zur Hand haben.
 *
 * Dasselbe Bauteil für Prüfbüro und Kundenportal; nur der Pfad ist
 * verschieden. Es zweimal zu schreiben hieße, es zweimal falsch machen
 * zu können.
 *
 * Der Ablauf beim Einrichten hat einen Punkt, der nicht abgekürzt werden
 * darf: **bestätigen**. Ein Geheimnis, das nur angezeigt und nie
 * nachgewiesen wurde, kann falsch übertragen sein — und das fiele erst
 * bei der nächsten Anmeldung auf, wenn niemand mehr hineinkommt.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import QRCode from 'qrcode'
import { api } from '../api/klient'
import { Fehlerhinweis, Ladeanzeige, datumDeutsch } from './Hinweise'

type FaktorZeile = {
  art: string
  bestaetigt_am: string | null
  zuletzt_benutzt_am: string | null
}

type Stand = {
  aktiv: boolean
  faktoren: FaktorZeile[]
  offene_wiederherstellungscodes: number
}

const ARTTEXT: Record<string, string> = {
  app: 'Authenticator-App',
  mail: 'Code per E-Mail',
}

export function Zweitfaktor({ pfad }: { pfad: string }) {
  const [stand, setzeStand] = useState<Stand | null>(null)
  const [fehler, setzeFehler] = useState<unknown>(null)
  const [laeuft, setzeLaeuft] = useState(false)
  const [einrichtung, setzeEinrichtung] = useState<{
    geheimnis: string
    adresse: string
  } | null>(null)
  const [code, setzeCode] = useState('')
  const [codes, setzeCodes] = useState<string[] | null>(null)

  const laden = useCallback(async () => {
    try {
      setzeStand(await api.holen<Stand>(pfad))
    } catch (f) {
      setzeFehler(f)
    }
  }, [pfad])

  useEffect(() => {
    void laden()
  }, [laden])

  async function tun<T>(arbeit: () => Promise<T>): Promise<T | undefined> {
    setzeFehler(null)
    setzeLaeuft(true)
    try {
      return await arbeit()
    } catch (f) {
      setzeFehler(f)
      return undefined
    } finally {
      setzeLaeuft(false)
    }
  }

  if (!stand) return <Ladeanzeige />

  const hatApp = stand.faktoren.some((f) => f.art === 'app')
  const hatMail = stand.faktoren.some((f) => f.art === 'mail')

  return (
    <div className="karte space-y-3">
      <h2 className="text-xl font-bold">Anmeldung in zwei Schritten</h2>
      <p className="text-base text-slate-700">
        Zusätzlich zum Passwort ein Code. Wer Ihr Passwort kennt, kommt damit
        allein nicht mehr hinein.
      </p>

      <Fehlerhinweis fehler={fehler} />

      {/* Die Wiederherstellungscodes: einmal und nie wieder. */}
      {codes && (
        <div className="rounded-feld border-2 border-stufe-2 bg-stufe-2-grund p-3">
          <p className="font-semibold text-stufe-2">
            Wiederherstellungscodes — bitte jetzt sichern
          </p>
          <p className="mt-1 text-base">
            Jeder gilt einmal und ersetzt den Code aus der App. Ohne sie ist
            ein verlorenes Telefon ein verlorener Zugang. Sie werden nur
            verschlüsselt gespeichert und lassen sich nicht noch einmal
            anzeigen.
          </p>
          <ul className="mt-2 grid gap-1 font-mono text-base sm:grid-cols-2">
            {codes.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              className="knopf-still px-3 text-base"
              onClick={() => void navigator.clipboard?.writeText(codes.join('\n'))}
            >
              Kopieren
            </button>
            <button
              type="button"
              className="knopf-still px-3 text-base"
              onClick={() => window.print()}
            >
              Drucken
            </button>
            <button
              type="button"
              className="knopf-haupt px-3 text-base"
              onClick={() => setzeCodes(null)}
            >
              Ich habe sie gesichert
            </button>
          </div>
        </div>
      )}

      {/* Die laufende Einrichtung der App. */}
      {einrichtung ? (
        <div className="rounded-feld border-2 border-akzent-500 p-3">
          <p className="font-semibold">Authenticator-App einrichten</p>
          <ol className="mt-2 list-decimal space-y-1 pl-5 text-base">
            <li>Öffnen Sie Ihre Authenticator-App und wählen Sie „Konto hinzufügen".</li>
            <li>Scannen Sie diesen Code — oder tippen Sie das Geheimnis ein.</li>
            <li>Geben Sie den angezeigten sechsstelligen Code unten ein.</li>
          </ol>

          <div className="mt-3 flex flex-wrap items-start gap-4">
            <QrBild adresse={einrichtung.adresse} />
            <div className="min-w-0">
              <div className="text-base text-slate-600">Geheimnis zum Abtippen</div>
              <code className="block break-all font-mono text-base">
                {einrichtung.geheimnis.match(/.{1,4}/g)?.join(' ')}
              </code>
              {/* Auf dem Handy öffnet dieser Verweis die App unmittelbar –
                  dort lässt sich kein QR-Code vom eigenen Bildschirm scannen. */}
              <a
                href={einrichtung.adresse}
                className="mt-2 inline-block text-marke-700 underline"
              >
                Auf diesem Gerät in der App öffnen
              </a>
            </div>
          </div>

          <div className="mt-3">
            <label htmlFor="zf-code">Code aus der App</label>
            <input
              id="zf-code"
              value={code}
              onChange={(e) => setzeCode(e.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
            />
          </div>

          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              className="knopf-haupt"
              disabled={laeuft || code.trim().length < 6}
              onClick={() =>
                void tun(async () => {
                  const antwort = await api.senden<{ codes: string[] }>(
                    `${pfad}/app/bestaetigen`,
                    { code: code.trim() },
                  )
                  setzeCodes(antwort.codes)
                  setzeEinrichtung(null)
                  setzeCode('')
                  await laden()
                })
              }
            >
              {laeuft ? 'Prüft …' : 'Bestätigen'}
            </button>
            <button
              type="button"
              className="knopf-still"
              onClick={() => {
                setzeEinrichtung(null)
                setzeCode('')
              }}
            >
              Abbrechen
            </button>
          </div>
        </div>
      ) : (
        <ul className="space-y-2">
          {(['app', 'mail'] as const).map((art) => {
            const vorhanden = stand.faktoren.find((f) => f.art === art)
            return (
              <li
                key={art}
                className="flex flex-wrap items-center gap-3 border-t-2 border-slate-100 pt-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-semibold">{ARTTEXT[art]}</div>
                  <div className="text-base text-slate-600">
                    {vorhanden
                      ? vorhanden.zuletzt_benutzt_am
                        ? `Zuletzt benutzt am ${datumDeutsch(vorhanden.zuletzt_benutzt_am)}`
                        : 'Eingerichtet'
                      : art === 'app'
                        ? 'Sechsstelliger Code, funktioniert ohne Netz'
                        : 'Code an Ihre E-Mail-Adresse — schwächer, aber ohne App'}
                  </div>
                </div>

                {vorhanden ? (
                  <button
                    type="button"
                    className="knopf-warnung px-3 text-base"
                    disabled={laeuft}
                    onClick={() => {
                      if (
                        window.confirm(
                          `${ARTTEXT[art]} abschalten?\n\nDanach genügt Ihr Passwort ` +
                            'wieder allein.',
                        )
                      ) {
                        void tun(async () => {
                          await api.loeschen(`${pfad}/${art}`)
                          await laden()
                        })
                      }
                    }}
                  >
                    Abschalten
                  </button>
                ) : (
                  <button
                    type="button"
                    className="knopf-zweit px-3 text-base"
                    disabled={laeuft}
                    onClick={() =>
                      void tun(async () => {
                        if (art === 'app') {
                          setzeEinrichtung(
                            await api.senden<{ geheimnis: string; adresse: string }>(
                              `${pfad}/app`,
                              {},
                            ),
                          )
                        } else {
                          await api.senden(`${pfad}/mail`, {})
                          await laden()
                        }
                      })
                    }
                  >
                    Einrichten
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {stand.aktiv && !einrichtung && (
        <div className="border-t-2 border-slate-100 pt-2">
          <div className="text-base text-slate-700">
            {stand.offene_wiederherstellungscodes} Wiederherstellungscodes übrig
            {stand.offene_wiederherstellungscodes <= 2 && (
              <span className="font-semibold text-stufe-2"> — Zeit für neue.</span>
            )}
          </div>
          <button
            type="button"
            className="knopf-still mt-1 px-3 text-base"
            disabled={laeuft}
            onClick={() => {
              if (
                window.confirm(
                  'Neue Wiederherstellungscodes erzeugen?\n\nDie bisherigen gelten ' +
                    'danach nicht mehr.',
                )
              ) {
                void tun(async () => {
                  const antwort = await api.senden<{ codes: string[] }>(
                    `${pfad}/wiederherstellungscodes`,
                    {},
                  )
                  setzeCodes(antwort.codes)
                  await laden()
                })
              }
            }}
          >
            Neue Codes erzeugen
          </button>
        </div>
      )}

      {hatApp && hatMail && (
        <p className="text-base text-slate-600">
          Beide Wege sind eingerichtet. Bei der Anmeldung genügt einer davon.
        </p>
      )}
    </div>
  )
}

/**
 * Der QR-Code als Bild.
 *
 * Auf eine Leinwand gezeichnet und nicht als Bilddatei vom Server geholt:
 * die otpauth-Adresse enthält das Geheimnis, und das soll den Browser
 * nicht noch einmal verlassen, nachdem es einmal angekommen ist.
 */
function QrBild({ adresse }: { adresse: string }) {
  const leinwand = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    if (!leinwand.current) return
    void QRCode.toCanvas(leinwand.current, adresse, {
      width: 180,
      margin: 1,
      // Fester Kontrast statt Farbwerten aus dem Thema: ein QR-Code muss
      // schwarz auf weiß sein, sonst liest ihn keine Kamera zuverlässig.
      color: { dark: '#000000', light: '#ffffff' },
    })
  }, [adresse])

  return (
    <canvas
      ref={leinwand}
      className="shrink-0 rounded-feld border-2 border-slate-200 bg-white p-1"
      aria-label="QR-Code zum Einrichten der Authenticator-App"
    />
  )
}
